import { useEffect, useState } from "react";
import { X, Search, GitMerge, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { resolveDuplicateGroup, scanDuplicateGroups } from "@/lib/attendeesApi";

const DEFAULT_CRITERIA = {
  email: true,
  exact_name: true,
  similar_name: true,
  company: false,
  phone: true,
};

const CRITERIA_OPTIONS = [
  { key: "email", label: "Same email address" },
  { key: "exact_name", label: "Same name (exact)" },
  { key: "similar_name", label: "Similar name (salutation / spelling)" },
  { key: "company", label: "Same company" },
  { key: "phone", label: "Same phone number" },
];

const MERGE_CORE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "company", label: "Company" },
  { key: "role", label: "Role" },
  { key: "ticket", label: "Ticket" },
  { key: "country", label: "Country" },
  { key: "mobile", label: "Mobile" },
  { key: "status", label: "Status" },
];

function confidenceStyles(level) {
  if (level === "high") return "bg-[#34D399]/15 text-[#34D399] border-[#34D399]/40";
  if (level === "medium") return "bg-[#F59E0B]/15 text-[#FCD34D] border-[#F59E0B]/40";
  return "bg-[var(--bg-elev)] text-[var(--text-tertiary)] border-[var(--border-default)]";
}

function memberLabel(m) {
  return `${m.name || "Unnamed"} · ${m.email || "no email"}`;
}

function initGroupState(grp) {
  const members = grp.members || [];
  const primary = members[0]?.id || "";
  const secondary = members[1]?.id || primary;
  const fieldSources = Object.fromEntries(MERGE_CORE_FIELDS.map((f) => [f.key, primary]));
  for (const m of members) {
    for (const key of Object.keys(m.customAnswers || {})) {
      fieldSources[`custom:${key}`] = fieldSources[`custom:${key}`] || primary;
    }
  }
  return {
    action: "keep_one",
    keepId: primary,
    deleteIds: members.map((m) => m.id).filter(Boolean),
    mergeTargetId: primary,
    mergeMode: "full",
    mergeSourceId: secondary,
    fieldSources,
  };
}

function ConfidenceBadge({ confidence }) {
  if (!confidence) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${confidenceStyles(confidence.level)}`}
      title={confidence.label}
    >
      {confidence.label}
      <span className="opacity-80">({confidence.score}%)</span>
    </span>
  );
}

export default function DedupeDialog({ eventId, open, onClose, onSuccess }) {
  const [criteria, setCriteria] = useState(DEFAULT_CRITERIA);
  const [groups, setGroups] = useState([]);
  const [groupState, setGroupState] = useState({});
  const [scanning, setScanning] = useState(false);
  const [busyGroup, setBusyGroup] = useState("");
  const [hasScanned, setHasScanned] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCriteria(DEFAULT_CRITERIA);
    setGroups([]);
    setGroupState({});
    setHasScanned(false);
  }, [open]);

  const runScan = async () => {
    if (!Object.values(criteria).some(Boolean)) {
      toast.error("Enable at least one match criterion");
      return;
    }
    setScanning(true);
    try {
      const data = await scanDuplicateGroups(eventId, criteria);
      const g = data.groups || [];
      setGroups(g);
      const next = {};
      for (const grp of g) next[grp.id] = initGroupState(grp);
      setGroupState(next);
      setHasScanned(true);
      if (!g.length) toast.message("No duplicate clusters found for these rules");
    } catch (err) {
      toast.error("Scan failed", { description: String(err.message || err) });
    } finally {
      setScanning(false);
    }
  };

  const updateGroup = (groupId, patch) => {
    setGroupState((prev) => ({ ...prev, [groupId]: { ...prev[groupId], ...patch } }));
  };

  const applyGroup = async (grp) => {
    const st = groupState[grp.id];
    if (!st) return;
    const memberIds = (grp.members || []).map((m) => m.id).filter(Boolean);
    setBusyGroup(grp.id);
    try {
      const body = { groupId: grp.id, memberIds, action: st.action };
      if (st.action === "keep_one") {
        if (!st.keepId) throw new Error("Choose a record to keep");
        body.keepId = st.keepId;
      } else if (st.action === "delete") {
        const deleteIds = (st.deleteIds || []).filter((id) => memberIds.includes(id));
        if (!deleteIds.length) throw new Error("Select at least one record to delete");
        body.deleteIds = deleteIds;
      } else if (st.action === "merge") {
        if (!st.mergeTargetId) throw new Error("Choose the survivor record");
        body.merge = {
          targetId: st.mergeTargetId,
          mode: st.mergeMode,
          sourceId: st.mergeSourceId,
          fieldSources: st.mergeMode === "pick" ? st.fieldSources : undefined,
        };
      }
      const result = await resolveDuplicateGroup(eventId, body);
      toast.success("Duplicate group resolved", {
        description: `Action: ${result.action || st.action}`,
      });
      setGroups((list) => list.filter((x) => x.id !== grp.id));
      onSuccess?.();
    } catch (err) {
      toast.error("Could not resolve group", { description: String(err.message || err) });
    } finally {
      setBusyGroup("");
    }
  };

  const customFieldsForGroup = (grp) => {
    const keys = new Set();
    for (const m of grp.members || []) {
      for (const k of Object.keys(m.customAnswers || {})) keys.add(k);
    }
    return Array.from(keys).sort();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" aria-label="Find duplicates">
      <div className="surface w-full max-w-5xl max-h-[92vh] flex flex-col border border-[var(--border-default)]">
        <div className="flex items-start justify-between gap-4 p-4 border-b border-[var(--border-default)] shrink-0">
          <div>
            <h2 className="font-heading text-xl">Find duplicate registrations</h2>
            <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-2xl">
              Choose what counts as a duplicate, scan, then keep one record, merge fields, or delete extras.
              Confidence reflects how strongly records match your rules.
            </p>
          </div>
          <button type="button" className="btn-ghost p-2 cursor-pointer" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 border-b border-[var(--border-default)] shrink-0 space-y-3">
          <div className="text-overline">Match criteria</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {CRITERIA_OPTIONS.map((opt) => (
              <label
                key={opt.key}
                className="flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] cursor-pointer border border-[var(--border-default)] px-3 py-2 hover:border-[var(--primary)]/40"
              >
                <input
                  type="checkbox"
                  className="accent-[var(--primary)]"
                  checked={Boolean(criteria[opt.key])}
                  onChange={(ev) => setCriteria((c) => ({ ...c, [opt.key]: ev.target.checked }))}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <button
            type="button"
            className="btn-primary text-sm cursor-pointer"
            disabled={scanning}
            onClick={runScan}
            data-testid="dedupe-scan"
          >
            <Search size={14} />
            {scanning ? "Scanning…" : hasScanned ? "Scan again" : "Scan for duplicates"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {!hasScanned ? (
            <p className="text-sm text-[var(--text-tertiary)]">Set match rules and run a scan to see duplicate clusters.</p>
          ) : null}
          {hasScanned && groups.length === 0 ? (
            <p className="text-sm text-[var(--text-secondary)]">No duplicate clusters found for the selected criteria.</p>
          ) : null}

          {groups.map((grp) => {
            const st = groupState[grp.id] || initGroupState(grp);
            const members = grp.members || [];
            const customFields = customFieldsForGroup(grp);
            const mergeFields = [
              ...MERGE_CORE_FIELDS,
              ...customFields.map((k) => ({ key: `custom:${k}`, label: k })),
            ];

            return (
              <div key={grp.id} className="border border-[var(--border-default)]">
                <div className="p-4 border-b border-[var(--border-default)] bg-[var(--bg-elev)] flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <ConfidenceBadge confidence={grp.confidence} />
                    <span className="text-xs font-mono text-[var(--text-tertiary)]">
                      {members.length} records
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(grp.signals || []).map((sig) => (
                      <span
                        key={`${grp.id}-${sig.label}`}
                        className="text-[10px] font-mono px-2 py-0.5 border border-[var(--border-default)] text-[var(--text-secondary)]"
                      >
                        {sig.label}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-4 flex flex-wrap gap-2 border-b border-[var(--border-default)]">
                  {[
                    { id: "keep_one", label: "Keep one", icon: UserCheck },
                    { id: "merge", label: "Merge", icon: GitMerge },
                    { id: "delete", label: "Delete", icon: Trash2 },
                  ].map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      className={`btn-secondary text-xs cursor-pointer ${st.action === id ? "ring-1 ring-[var(--primary)]" : ""}`}
                      onClick={() => updateGroup(grp.id, { action: id })}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>

                <ul className="divide-y divide-[var(--border-default)]">
                  {members.map((m) => (
                    <li key={m.id} className="p-4 flex flex-wrap gap-3 items-start text-sm">
                      {st.action === "keep_one" ? (
                        <input
                          type="radio"
                          name={`keep-${grp.id}`}
                          className="accent-[var(--primary)] mt-1"
                          checked={st.keepId === m.id}
                          onChange={() => updateGroup(grp.id, { keepId: m.id })}
                        />
                      ) : null}
                      {st.action === "delete" ? (
                        <input
                          type="checkbox"
                          className="accent-[var(--primary)] mt-1"
                          checked={(st.deleteIds || []).includes(m.id)}
                          onChange={() => {
                            const set = new Set(st.deleteIds || []);
                            if (set.has(m.id)) set.delete(m.id);
                            else set.add(m.id);
                            updateGroup(grp.id, { deleteIds: [...set] });
                          }}
                        />
                      ) : null}
                      <div className="flex-1 min-w-[200px]">
                        <div className="font-medium">{m.name || "Unnamed"}</div>
                        <div className="text-xs font-mono text-[var(--text-tertiary)] mt-0.5">
                          {m.email} · {m.company || "—"} · {m.mobile || "—"} · {m.status}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {st.action === "merge" ? (
                  <div className="p-4 border-t border-[var(--border-default)] space-y-3 bg-[var(--bg-elev)]/50">
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="text-xs text-[var(--text-secondary)]">
                        Survivor (merged record kept)
                        <select
                          className="input-dark w-full mt-1 text-xs"
                          value={st.mergeTargetId}
                          onChange={(ev) => updateGroup(grp.id, { mergeTargetId: ev.target.value })}
                        >
                          {members.map((m) => (
                            <option key={m.id} value={m.id}>{memberLabel(m)}</option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs text-[var(--text-secondary)]">
                        Merge mode
                        <select
                          className="input-dark w-full mt-1 text-xs"
                          value={st.mergeMode}
                          onChange={(ev) => updateGroup(grp.id, { mergeMode: ev.target.value })}
                        >
                          <option value="full">Full merge — all fields from one record</option>
                          <option value="pick">Pick fields — choose source per field</option>
                        </select>
                      </label>
                    </div>
                    {st.mergeMode === "full" ? (
                      <label className="text-xs text-[var(--text-secondary)] block">
                        Copy all fields from
                        <select
                          className="input-dark w-full mt-1 text-xs"
                          value={st.mergeSourceId}
                          onChange={(ev) => updateGroup(grp.id, { mergeSourceId: ev.target.value })}
                        >
                          {members.filter((m) => m.id !== st.mergeTargetId).map((m) => (
                            <option key={m.id} value={m.id}>{memberLabel(m)}</option>
                          ))}
                          {members.length === 1 ? (
                            <option value={st.mergeTargetId}>{memberLabel(members[0])}</option>
                          ) : null}
                        </select>
                      </label>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-[var(--text-tertiary)] font-mono uppercase tracking-widest">
                              <th className="py-2 pr-3">Field</th>
                              <th className="py-2">Use value from</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mergeFields.map((field) => (
                              <tr key={field.key} className="border-t border-[var(--border-default)]">
                                <td className="py-2 pr-3 font-mono text-[var(--text-secondary)]">{field.label}</td>
                                <td className="py-2">
                                  <select
                                    className="input-dark w-full"
                                    value={st.fieldSources?.[field.key] || st.mergeTargetId}
                                    onChange={(ev) =>
                                      updateGroup(grp.id, {
                                        fieldSources: {
                                          ...st.fieldSources,
                                          [field.key]: ev.target.value,
                                        },
                                      })
                                    }
                                  >
                                    {members.map((m) => (
                                      <option key={m.id} value={m.id}>{memberLabel(m)}</option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="p-4 border-t border-[var(--border-default)] flex justify-end">
                  <button
                    type="button"
                    className="btn-primary text-xs cursor-pointer"
                    disabled={busyGroup === grp.id}
                    onClick={() => applyGroup(grp)}
                  >
                    {busyGroup === grp.id ? "Applying…" : "Apply to this cluster"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
